import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles, Plus, Pencil, Trash2, Save, X, Megaphone, Mail, Eye, Rss, Copy } from 'lucide-react';
import authedFetch from '../lib/authedFetch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import PageShell from '../components/layout/PageShell';
import PageHero from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'feature', label: 'Feature' },
  { value: 'ux', label: 'UX / Polish' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'fix', label: 'Bug fix' },
  { value: 'security', label: 'Security' },
];

const emptyForm = { id: '', title: '', body: '', category: 'feature', date: '', notify_slack: true };

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function ReleaseNotesAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | emptyForm | existing item
  const [saving, setSaving] = useState(false);
  const [slackConfigured, setSlackConfigured] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const pageTheme = useMemo(() => getLightSectionTheme('Navy'), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [notesRes, slackRes] = await Promise.all([
        authedFetch(`${API}/api/release-notes?limit=50`),
        authedFetch(`${API}/api/release-notes/slack-status`),
      ]);
      const data = await notesRes.json();
      setItems(data?.items || []);
      if (slackRes.ok) {
        const s = await slackRes.json();
        setSlackConfigured(Boolean(s?.slack_configured));
      }
    } catch (e) {
      toast.error('Failed to load release notes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = useCallback(async () => {
    if (!editing) return;
    if (!editing.title.trim() || !editing.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSaving(true);
    try {
      const isUpdate = Boolean(editing.id);
      const url = isUpdate
        ? `${API}/api/release-notes/${editing.id}`
        : `${API}/api/release-notes`;
      const res = await authedFetch(url, {
        method: isUpdate ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editing.title.trim(),
          body: editing.body.trim(),
          category: editing.category,
          date: editing.date || todayIso(),
          ...(isUpdate ? {} : { notify_slack: Boolean(editing.notify_slack) }),
        }),
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail);
      }
      const result = await res.json();
      const slackQueued = !isUpdate && result?.item?.slack_broadcast;
      toast.success(
        isUpdate
          ? 'Release note updated'
          : slackQueued
          ? 'Release note created · Slack broadcast queued'
          : 'Release note created'
      );
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }, [editing, load]);

  const onDelete = useCallback(
    async (id) => {
      if (!window.confirm('Delete this release note?')) return;
      try {
        const res = await authedFetch(`${API}/api/release-notes/${id}`, {
          method: 'DELETE',
        });
        if (!res.ok) throw new Error(await res.text());
        toast.success('Deleted');
        await load();
      } catch (e) {
        toast.error(`Delete failed: ${e.message || e}`);
      }
    },
    [load]
  );

  const onCopyFeedUrl = useCallback(async () => {
    const url = `${API}/api/release-notes/feed.xml`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('RSS feed URL copied');
    } catch {
      window.prompt('Copy the RSS feed URL:', url);
    }
  }, []);

  const onPreviewDigest = useCallback(async () => {
    try {
      const res = await authedFetch(`${API}/api/release-notes/digest/preview?window_days=7`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const w = window.open('', '_blank', 'noopener,noreferrer,width=720,height=900');
      if (w) {
        w.document.open();
        w.document.write(data.html);
        w.document.close();
      }
    } catch (e) {
      toast.error(`Preview failed: ${e.message || e}`);
    }
  }, []);

  const onSendDigest = useCallback(async () => {
    if (
      !window.confirm(
        'Send the weekly release notes digest to all super-admins right now?'
      )
    )
      return;
    setSendingDigest(true);
    try {
      const res = await authedFetch(`${API}/api/release-notes/digest/send-now?window_days=7`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data.skipped_reason === 'resend_not_configured') {
        toast.info('Digest skipped: RESEND_API_KEY not set');
      } else if (data.skipped_reason === 'no_recipients') {
        toast.info('Digest skipped: no super-admin recipients found');
      } else {
        toast.success(
          `Digest sent to ${data.sent}/${data.recipients} super-admin${
            data.recipients === 1 ? '' : 's'
          } (${data.notes} notes)`
        );
      }
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSendingDigest(false);
    }
  }, []);

  const stats = useMemo(
    () => [
      { label: 'Total notes', value: items.length.toString() },
      {
        label: 'Last published',
        value: items[0]?.date
          ? new Date(items[0].date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
      },
      {
        label: 'Most recent category',
        value: items[0]?.category
          ? items[0].category.charAt(0).toUpperCase() + items[0].category.slice(1)
          : '—',
      },
    ],
    [items]
  );

  return (
    <PageShell
      theme={pageTheme}
      hero={(
        <PageHero
          theme={pageTheme}
          icon={Megaphone}
          title="Release Notes"
          description="Publish What's-New entries for the sidebar popover. Super Admins only."
          stats={stats}
          testId="release-notes-hero"
        />
      )}
      data-testid="release-notes-admin-page"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 py-8">
        <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
          <div className="flex min-w-0 items-center gap-2 text-slate-700">
            <Rss className="h-4 w-4 flex-shrink-0 text-amber-500" />
            <span className="truncate">
              Public RSS feed:{' '}
              <code className="rounded bg-white px-1.5 py-0.5 text-[12px] text-slate-800 ring-1 ring-slate-200">
                {API}/api/release-notes/feed.xml
              </code>
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCopyFeedUrl}
            data-testid="release-notes-copy-feed-btn"
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
          </Button>
        </div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Published notes</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={onPreviewDigest}
              data-testid="release-notes-preview-digest-btn"
            >
              <Eye className="mr-2 h-4 w-4" /> Preview digest
            </Button>
            <Button
              variant="outline"
              onClick={onSendDigest}
              disabled={sendingDigest}
              data-testid="release-notes-send-digest-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              {sendingDigest ? 'Sending…' : 'Send digest now'}
            </Button>
            <Button
              onClick={() =>
                setEditing({ ...emptyForm, date: todayIso() })
              }
              data-testid="release-notes-new-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> New release note
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
            <Sparkles className="mx-auto mb-3 h-8 w-8 text-amber-400" />
            <p className="text-sm font-medium text-slate-700">
              No release notes yet
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Create your first entry to start showing updates in the sidebar.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((it) => (
              <li
                key={it.id}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                data-testid={`release-note-row-${it.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                        {it.category}
                      </span>
                      <span className="text-xs text-slate-500">{it.date}</span>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {it.title}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-600">{it.body}</p>
                    {it.created_by && (
                      <p className="mt-1 text-[11px] text-slate-400">
                        By {it.created_by}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing({ ...it })}
                      data-testid={`release-note-edit-${it.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(it.id)}
                      data-testid={`release-note-delete-${it.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {editing.id ? 'Edit release note' : 'New release note'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(null)}
                data-testid="release-note-cancel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="rn-title">Title</Label>
                <Input
                  id="rn-title"
                  data-testid="release-note-title-input"
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                  maxLength={120}
                  placeholder="e.g. New dashboard pinning"
                />
              </div>
              <div>
                <Label htmlFor="rn-body">Description</Label>
                <Textarea
                  id="rn-body"
                  data-testid="release-note-body-input"
                  rows={3}
                  maxLength={500}
                  value={editing.body}
                  onChange={(e) =>
                    setEditing({ ...editing, body: e.target.value })
                  }
                  placeholder="Short summary of what's new."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="rn-category">Category</Label>
                  <Select
                    value={editing.category}
                    onValueChange={(v) =>
                      setEditing({ ...editing, category: v })
                    }
                  >
                    <SelectTrigger
                      id="rn-category"
                      data-testid="release-note-category-select"
                    >
                      <SelectValue placeholder="Pick a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="rn-date">Date</Label>
                  <Input
                    id="rn-date"
                    type="date"
                    data-testid="release-note-date-input"
                    value={editing.date || ''}
                    onChange={(e) =>
                      setEditing({ ...editing, date: e.target.value })
                    }
                  />
                </div>
              </div>
              {!editing.id && (
                <label
                  className={cn(
                    'flex items-start gap-3 rounded-md border p-3 text-sm',
                    slackConfigured
                      ? 'border-slate-200 bg-slate-50'
                      : 'border-dashed border-slate-300 bg-slate-50/50 text-slate-500'
                  )}
                  data-testid="release-note-slack-toggle"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600"
                    checked={Boolean(editing.notify_slack)}
                    disabled={!slackConfigured}
                    onChange={(e) =>
                      setEditing({ ...editing, notify_slack: e.target.checked })
                    }
                  />
                  <span>
                    <span className="block font-medium text-slate-800">
                      {slackConfigured
                        ? 'Broadcast to Slack ops channel'
                        : 'Slack broadcast unavailable'}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {slackConfigured
                        ? 'A short message with the title, category and body will be posted via OPS_SLACK_WEBHOOK_URL.'
                        : 'Set OPS_SLACK_WEBHOOK_URL to enable one-click broadcasts.'}
                    </span>
                  </span>
                </label>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditing(null)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={saving}
                data-testid="release-note-save"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
